import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Add, Remove } from '@mui/icons-material';
import { Nullable, QueryListResponse } from 'global';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Button, IconButton, Stack, Typography, Tooltip } from '@mui/material';

import { ActionsProps } from './types';
import { StageNotes } from '../stagesNotes';
import { bpfAPI, Stage } from '../../api';
import { CompletedBadge } from './CompletedBadge';
import { EmptyAttributes } from './EmptyAttributes';
import { BPFAPINameSpase, StageNote } from '../../types';
import { calcFieldsAmount, checkIsStageFieldsExist } from '../../lib';
import { StageAttributesContainer } from './attributes/StageAttributesContainer';

interface Props extends ActionsProps {
  parentId: string;
  stage: Nullable<Stage>;
  parentType: BpfLinkType;
  canReopenStage: boolean;
  isStagesUpdating: boolean;
  isStageCompleting: boolean;
  isStageUnCompleting: boolean;
  isCompleteDisabled: boolean;
}

export const StageView = ({
  stage,
  actions,
  parentId,
  parentType,
  canReopenStage,
  isStagesUpdating,
  isStageCompleting,
  isStageUnCompleting,
  isCompleteDisabled,
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const { data, loading: isNotesLoading } = useQuery<BPFAPINameSpase<QueryListResponse<StageNote>>>(
    bpfAPI.getStageNotes,
    {
      notifyOnNetworkStatusChange: true,
      skip: !stage?.businessProcessFlowStageId,
      variables: { input: { parentId, parentType, stageId: stage?.businessProcessFlowStageId! } },
    }
  );

  if (!stage) return null;

  const { requiredFields, requiredCompletedFields } = calcFieldsAmount(
    stage.fieldValues?.values?.length ? stage.fieldValues.values : stage.stageFields
  );

  return (
    <>
      <Box className="currentStages">
        <Stack direction="row" alignItems="center">
          <IconButton size="small" onClick={() => setIsCollapsed(prevState => !prevState)}>
            {!isCollapsed ? <Remove fontSize="small" /> : <Add fontSize="small" />}
          </IconButton>

          <Typography variant="subtitle2" component="span">
            {stage.name}
          </Typography>
        </Stack>

        <RenderWithCondition
          condition={!stage?.fieldValues?.isCompleted}
          fallBack={
            <Stack direction="row" gap="0.5rem" alignItems="center">
              <CompletedBadge stage={stage} />

              <RenderWithCondition condition={canReopenStage}>
                <Button
                  size="small"
                  variant="outlined"
                  loading={isStageUnCompleting}
                  onClick={() => actions.onUnCompleteStage(stage.businessProcessFlowStageId, parentId, parentType)}
                >
                  Reopen stage
                </Button>
              </RenderWithCondition>
            </Stack>
          }
        >
          <Tooltip
            title={
              requiredFields !== requiredCompletedFields ? 'Complete Required Attributes before Complete Stage' : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                loading={isStageCompleting}
                disabled={isCompleteDisabled || requiredFields !== requiredCompletedFields}
                onClick={() => actions.onCompleteStage(stage.businessProcessFlowStageId, parentId, parentType)}
              >
                Complete Stage
              </Button>
            </span>
          </Tooltip>
        </RenderWithCondition>
      </Box>

      {!isCollapsed && (
        <Box className="attributes">
          <StageNotes
            parentId={parentId}
            parentType={parentType}
            loading={isNotesLoading}
            key={JSON.stringify({ isNotesLoading })}
            apiNotes={data?.bpf?.result?.list ?? []}
            stageId={stage.businessProcessFlowStageId}
          />

          <RenderWithCondition condition={checkIsStageFieldsExist(stage)} fallBack={<EmptyAttributes />}>
            <StageAttributesContainer
              stage={stage}
              parentId={parentId}
              parentType={parentType}
              isLoading={isStagesUpdating}
              onUpdateStageAttributes={actions.onUpdateStageAttributes}
            />
          </RenderWithCondition>
        </Box>
      )}
    </>
  );
};
